import React, { useState, useCallback, useEffect } from 'react';
import { Box, Typography, IconButton, Dialog, DialogContent, Tooltip, CircularProgress } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

const ImageViewer = ({ docs }) => {
  const [files, setFiles] = useState(docs || []);
  const [previewFile, setPreviewFile] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  // Auto-hide loader
  useEffect(() => {
    if (files.length > 0) {
      setLoading(true);
      const timeout = setTimeout(() => setLoading(false), 6000);
      return () => clearTimeout(timeout);
    }
  }, [files]);

  const onDrop = useCallback(acceptedFiles => {
    const newFiles = acceptedFiles.map(file => ({
      file,
      url: URL.createObjectURL(file),
      type: file.type,
      name: file.name,
    }));
    setFiles(prev => [...prev, ...newFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
  });

  const handleDelete = targetFile => {
    setFiles(prev => prev.filter(f => f !== targetFile));
  };

  const isViewable = file => file.type?.startsWith('image/') || file.type === 'application/pdf';

  const renderDialogPreview = () => {
    if (!previewFile) return null;
    if (previewFile.type?.startsWith('image/')) {
      return (
        <img src={previewFile.url} alt="preview" style={{ maxHeight: '80vh', maxWidth: '100%', objectFit: 'contain' }} />
      );
    } else if (previewFile.type === 'application/pdf') {
      return <iframe src={previewFile.url} title="preview" style={{ height: '80vh', width: '100%', border: 'none' }} />;
    } else {
      return <Typography color="white">Cannot preview this file.</Typography>;
    }
  };

  return (
    <>
      <Box
        sx={{
          border: '2px dashed #ccc',
          borderRadius: 2,
          p: 2,
          textAlign: 'center',
          backgroundColor: isDragActive ? '#e3f2fd' : '#fafafa',
          mb: 2,
          cursor: 'pointer',
        }}
        {...getRootProps()}>
        <input {...getInputProps()} />
        <Typography variant="body2">
          {isDragActive ? 'Drop the files here...' : 'Drag & drop or click to upload files'}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 2,
          overflowX: 'auto',
          minHeight: 100,
          mb: 2,
          py: 1,
        }}>
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <CircularProgress size={24} />
            <Typography sx={{ ml: 1 }}>Loading files...</Typography>
          </Box>
        ) : files.length === 0 ? (
          <Typography sx={{ ml: 1 }}>No files uploaded</Typography>
        ) : (
          files.map((file, idx) => {
            const isImage = file.type?.startsWith('image/');
            const isPDF = file.type === 'application/pdf';
            return (
              <Box
                key={idx}
                sx={{
                  position: 'relative',
                  width: 100,
                  height: 80,
                  p: 1,
                  border: '1px solid #ccc',
                  borderRadius: 1,
                  bgcolor: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  if (isViewable(file)) {
                    setPreviewFile(file);
                    setOpenDialog(true);
                  }
                }}>
                <Tooltip title={file.name}>
                  <Box>
                    {isImage ? (
                      <img
                        src={file.url}
                        alt="file"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }}
                      />
                    ) : isPDF ? (
                      <PictureAsPdfIcon sx={{ fontSize: 40, color: 'red' }} />
                    ) : (
                      <InsertDriveFileIcon sx={{ fontSize: 40, color: 'gray' }} />
                    )}
                  </Box>
                </Tooltip>

                <IconButton
                  onClick={e => {
                    e.stopPropagation();
                    handleDelete(file);
                  }}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    color: 'white',
                    bgcolor: 'rgba(0,0,0,0.5)',
                    '&:hover': { bgcolor: 'red' },
                  }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            );
          })
        )}
      </Box>

      {/* Dialog Preview */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="md">
        <DialogContent sx={{ position: 'relative', bgcolor: 'black', p: 2 }}>
          <IconButton onClick={() => setOpenDialog(false)} sx={{ position: 'absolute', top: 8, right: 8, color: 'white' }}>
            <CloseIcon />
          </IconButton>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>{renderDialogPreview()}</Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ImageViewer;
